import {
  AppBadge,
  AppBadgeForID,
  AppCombobox,
  AppDataTable,
  type AppDataTableColumn,
  AppDialog,
  AppInput,
  AppSearchBox,
  AppSelect,
  AppSelectPopover,
  AppTextarea,
  type BadgeColor,
  FormLabel,
  type SelectOption,
} from '@9b3kuq5phwuk4txw/kiban-react-ui'
import { recruitmentTypeOptions, toRecruitmentTypeFormValue } from '@inquiries/-lib/recruitment-type-options'
import { CheckIcon } from 'lucide-react'
import {
  INDUSTRY_GROUPS,
  INQUIRY_PRIORITY_COLOR_MAP,
  INQUIRY_PRIORITY_LABEL_MAP,
  type InquiryPriority,
  PKS_MESSAGES,
} from 'shared'
import { AppStartAsapCheckbox } from '@/components/AppStartAsapCheckbox'
import { AppWorkLocationCheckboxGroup } from '@/components/AppWorkLocationCheckboxGroup'
import { useFormattedNumberInput } from '@/hooks/useFormattedNumberInput'
import type { SalesforceDeal } from '@/lib'
import { BadgeForAnoStatus } from '../BadgeForAnoStatus'
import { MemberCombobox } from '../MemberCombobox'
import { useCreateDealDialog } from './hooks/useCreateDealDialog'

const dealColumns: AppDataTableColumn<SalesforceDeal>[] = [
  { key: 'customer_name', label: '顧客名', accessor: (row) => row.customer_name, sortable: false },
  {
    key: 'salesforce_deal_name',
    label: '商談名',
    sortable: false,
    render: (row) => (
      <div className='flex items-center justify-between gap-2'>
        <span>{row.salesforce_deal_name}</span>
        <span className='flex shrink-0 items-center gap-1'>
          {row.has_inquiry && (
            <AppBadge color='orange' outline>
              引合登録済
            </AppBadge>
          )}
          {row.has_ano && (
            <AppBadge color='primary' outline>
              ANOあり
            </AppBadge>
          )}
        </span>
      </div>
    ),
  },
]

const priorityBadgeOptions: SelectOption[] = (Object.keys(INQUIRY_PRIORITY_LABEL_MAP) as InquiryPriority[]).map(
  (key) => ({
    value: key,
    label: INQUIRY_PRIORITY_LABEL_MAP[key],
    color: INQUIRY_PRIORITY_COLOR_MAP[key] as BadgeColor,
  }),
)

type CreateDealDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CreateDealDialog({ open, onOpenChange }: CreateDealDialogProps) {
  const {
    step,
    customerId,
    customerName,
    customerSearchInput,
    setCustomerSearchInput,
    customerQuery,
    isFetchingCustomers,
    dealName,
    setDealName,
    selectedId,
    customersData,
    dealResults,
    handleCustomerChange,
    handleRowClick,
    dealOptions,
    handleDealChangeInStep2,
    anoProjectId,
    setAnoProjectId,
    overview,
    setOverview,
    requirements,
    setRequirements,
    contactId,
    setContactId,
    coordinationMemberId,
    setCoordinationMemberId,
    primaryDivisionId,
    setPrimaryDivisionId,
    salesMemberId,
    setSalesMemberId,
    serviceManagerMemberId,
    setServiceManagerMemberId,
    startOn,
    setStartOn,
    isStartAsap,
    setIsStartAsap,
    endOn,
    setEndOn,
    estimatedAmount,
    setEstimatedAmount,
    industryName,
    setIndustryName,
    station,
    setStation,
    workLocations,
    setWorkLocations,
    priority,
    setPriority,
    recruitmentType,
    setRecruitmentType,
    anos,
    hasEnabledAnos,
    isLoadingAnos,
    contactOptions,
    coordinationMemberOptions,
    salesMemberOptions,
    smgMemberOptions,
    divisionOptions,
    formErrors,
    hasErrors,
    isPending,
    handleClose,
    handleNext,
    handleBack,
    handleRegister,
  } = useCreateDealDialog(onOpenChange)

  const estimatedAmountInputProps = useFormattedNumberInput(estimatedAmount, setEstimatedAmount)

  const customerOptions =
    customersData?.customers.map((c) => ({
      value: String(c.id),
      label: c.name,
      leftItem: <AppBadgeForID value={c.client_id} tooltipContent='クライアントコード' />,
    })) ?? []

  // 候補が limit を超えた場合、絞り込みを促すヘルプテキストを表示する
  const customerTotal = customersData?.total ?? 0
  const customerHasMore = customerTotal > customerOptions.length
  const customerHelpText = customerHasMore
    ? `候補が ${customerTotal} 件あります。検索文字を絞り込んでください（先頭 ${customerOptions.length} 件のみ表示）`
    : undefined

  const customerSelectPlaceholder = (() => {
    if (customerQuery === '') return '顧客名を検索してください'
    if (isFetchingCustomers) return '読み込み中...'
    if (customerOptions.length === 0) return '該当する顧客がありません'
    return '顧客を選択'
  })()

  const dealComboboxOptions = dealOptions.map((d) => {
    const badges = (
      <span key={d.value} className='flex gap-1'>
        {d.hasInquiry && (
          <AppBadge color='orange' outline>
            引合登録済
          </AppBadge>
        )}
        {d.hasAno && (
          <AppBadge color='primary' outline>
            ANOあり
          </AppBadge>
        )}
      </span>
    )
    return {
      ...d,
      rightItem: d.hasAno || d.hasInquiry ? badges : undefined,
    }
  })

  const anoOptions = anos.map((a) => ({
    value: String(a.salesforce_project_id),
    label: a.name,
    disabled: !a.enabled,
    rightItem: (
      <BadgeForAnoStatus inquiryStatus={a.inquiry_status} salesforceProjectId={a.salesforce_project_id} ano={a.ano} />
    ),
  }))

  type FooterOption = {
    label: string
    onClick: () => void
    variant?: 'outline'
    disabled?: boolean
    isPending?: boolean
    isPendingText?: string
  }
  const footerOptions: [FooterOption, ...FooterOption[]] =
    step === 'select-deal'
      ? [
          { label: 'キャンセル', onClick: handleClose, variant: 'outline' },
          { label: '次へ', onClick: handleNext, disabled: !selectedId },
        ]
      : [
          { label: '戻る', onClick: handleBack, variant: 'outline', disabled: isPending },
          { label: 'キャンセル', onClick: handleClose, variant: 'outline', disabled: isPending },
          {
            label: '登録',
            onClick: handleRegister,
            disabled: hasErrors,
            isPending,
            isPendingText: '登録中...',
          },
        ]

  return (
    <AppDialog
      open={open}
      onOpenChange={handleClose}
      title={step === 'select-deal' ? '商談選択' : '引合情報入力'}
      className='sm:max-w-175 h-[90vh] flex flex-col'
      footerOptions={footerOptions}
      isPending={isPending}
    >
      {step === 'select-deal' ? (
        <div className='grid min-h-0 flex-1 grid-rows-[auto_1fr] gap-4 py-4'>
          {/* 検索条件 */}
          <div className='space-y-3'>
            {/* 顧客名: 検索バーに入力 → 300ms 後に API 発火 → セレクトで選択 */}
            <div className='space-y-1'>
              <FormLabel htmlFor='' required>
                顧客名
              </FormLabel>
              <AppSearchBox
                value={customerSearchInput}
                onChange={setCustomerSearchInput}
                placeholder='顧客名を入力してください'
                tooltipContent='入力すると顧客名で候補を検索します'
              />
              <AppSelect
                id='customer'
                value={customerId}
                onValueChange={handleCustomerChange}
                options={customerOptions}
                placeholder={customerSelectPlaceholder}
                disabled={customerQuery === '' || isFetchingCustomers || customerOptions.length === 0}
                helpText={customerHelpText}
              />
            </div>
            <AppInput
              id='deal-name'
              label='商談名'
              value={dealName}
              onChange={(e) => setDealName(e.target.value)}
              placeholder='商談名で絞り込み'
            />
          </div>

          {/* 検索結果 */}
          <div className='min-h-0 overflow-auto'>
            <AppDataTable<SalesforceDeal>
              data={dealResults}
              columns={dealColumns}
              paginated={false}
              emptyMessage={customerId ? '該当する商談が見つかりませんでした' : '顧客名を選択してください'}
              getRowKey={(row) => row.id}
              onRowClick={handleRowClick}
              getRowClassName={(deal) => (selectedId === deal.id ? 'bg-selected' : undefined)}
              selectedRowKey={selectedId}
            />
          </div>
        </div>
      ) : (
        <div className='min-h-0 flex-1 space-y-4 overflow-y-auto p-4'>
          {/* 選択済み商談情報 */}
          <div className='space-y-2 rounded border bg-muted/50 p-3 text-sm'>
            <p>
              <span className='text-muted-foreground mr-1'>顧客名：</span>
              {customerName}
            </p>
            <div className='flex items-start gap-1'>
              <span className='text-muted-foreground shrink-0'>
                商談名：<span className='text-error'>*</span>
              </span>
              <div className='min-w-0 flex-1'>
                <AppCombobox
                  value={selectedId ? String(selectedId) : ''}
                  onValueChange={handleDealChangeInStep2}
                  options={dealComboboxOptions}
                  placeholder='商談を選択'
                  field={{ required: true, error: formErrors.dealId }}
                  search={{ placeholder: '商談名で検索...', emptyMessage: '商談が見つかりません' }}
                  customization={{
                    renderOption: (option, isSelected) => (
                      <>
                        <span className='flex-1'>{option.label}</span>
                        {option.rightItem && <span className='ml-2'>{option.rightItem}</span>}
                        <CheckIcon className={`ml-2 size-4 shrink-0 ${isSelected ? '' : 'invisible'}`} />
                      </>
                    ),
                  }}
                />
              </div>
            </div>
          </div>

          {/* ANO選択 */}
          <AppSelect
            id='ano'
            label='ANO'
            required={hasEnabledAnos}
            value={anoProjectId}
            onValueChange={setAnoProjectId}
            options={anoOptions}
            placeholder={isLoadingAnos ? '読み込み中...' : 'ANOを選択'}
            disabled={isLoadingAnos || anoOptions.length <= 1}
            error={formErrors.anoProjectId}
          />

          {/* 案件概要 */}
          <AppTextarea
            id='overview'
            label='案件概要'
            value={overview}
            onChange={(e) => setOverview(e.target.value)}
            placeholder='案件の内容を入力'
            helpText='どんな案件か（商談の説明、背景、対象システム）'
            rows={3}
            error={formErrors.overview}
          />

          {/* アサイン要件 */}
          <AppTextarea
            id='requirements'
            label='アサイン要件'
            required
            value={requirements}
            onChange={(e) => setRequirements(e.target.value)}
            placeholder='求める人材の条件を入力'
            helpText='どんな人が欲しいか（スキル、経験年数、人数）'
            rows={4}
            error={formErrors.requirements}
          />

          {/* 取引先責任者 */}
          <AppSelect
            id='contact'
            label='取引先責任者'
            value={contactId}
            onValueChange={setContactId}
            options={contactOptions}
            placeholder='取引先責任者を選択'
            clearable
            onClear={() => setContactId('')}
          />

          {/* 主管部門 */}
          <AppCombobox
            value={primaryDivisionId}
            onValueChange={setPrimaryDivisionId}
            options={divisionOptions}
            placeholder='部門を選択'
            clearable
            field={{ label: '主管部門' }}
            search={{ placeholder: '部門名で検索...', emptyMessage: '部門が見つかりません' }}
          />

          {/* 営業担当 */}
          <MemberCombobox
            value={salesMemberId}
            onValueChange={setSalesMemberId}
            options={salesMemberOptions}
            label='営業担当'
            disabled={hasEnabledAnos}
            placeholder={hasEnabledAnos && !salesMemberId ? PKS_MESSAGES.MEMBER_NOT_REGISTERED : undefined}
          />

          {/* サービスマネージャー */}
          <MemberCombobox
            value={serviceManagerMemberId}
            onValueChange={setServiceManagerMemberId}
            options={smgMemberOptions}
            label='SMG担当'
            disabled={hasEnabledAnos}
            placeholder={hasEnabledAnos && !serviceManagerMemberId ? PKS_MESSAGES.MEMBER_NOT_REGISTERED : undefined}
            message={hasEnabledAnos ? PKS_MESSAGES.EDIT_SALES_SMG_IN_PKS : undefined}
          />

          {/* 調達・調整担当 */}
          <MemberCombobox
            value={coordinationMemberId}
            onValueChange={setCoordinationMemberId}
            options={coordinationMemberOptions}
            label='調達・調整担当'
          />

          {/* 期間 */}
          <div className='space-y-2'>
            <div className='grid grid-cols-2 gap-4'>
              <AppInput
                id='start-on'
                label='開始日'
                type='date'
                value={isStartAsap ? '' : startOn}
                onChange={(e) => setStartOn(e.target.value)}
                disabled={isStartAsap}
              />
              <AppInput
                id='end-on'
                label='終了日'
                type='date'
                value={endOn}
                onChange={(e) => setEndOn(e.target.value)}
                error={formErrors.endOn}
              />
            </div>
            <AppStartAsapCheckbox checked={isStartAsap} onCheckedChange={setIsStartAsap} />
          </div>

          {/* 想定金額 */}
          <AppInput
            id='estimated-amount'
            label='想定金額'
            {...estimatedAmountInputProps}
            placeholder='金額を入力'
            error={formErrors.estimatedAmount}
          />

          {/* 業界 */}
          <AppSelect
            id='industry-name'
            label='業界'
            value={industryName}
            onValueChange={setIndustryName}
            options={INDUSTRY_GROUPS.map((group) => ({ value: group, label: group }))}
            placeholder='選択してください'
            helpText='エンドユーザーの業界を選びます（顧客が SIer の場合はその先の業界）'
            clearable
            onClear={() => setIndustryName('')}
            error={formErrors.industryName}
          />

          {/* 最寄駅 */}
          <AppInput
            id='station'
            label='最寄駅'
            value={station}
            onChange={(e) => setStation(e.target.value)}
            placeholder='最寄駅を入力'
            error={formErrors.station}
          />

          {/* 勤務形態 */}
          <AppWorkLocationCheckboxGroup gap='gap-6' value={workLocations} onChange={setWorkLocations} />

          {/* 優先度・募集形態 */}
          <div className='grid grid-cols-2 gap-4'>
            <AppSelectPopover
              label='優先度'
              currentValue={priority}
              options={priorityBadgeOptions}
              onSelect={(value) => setPriority(value as InquiryPriority)}
              header='優先度を選択'
              contentClassName='w-36'
              triggerVariant='button'
              placeholder='優先度を選択'
            />
            <AppSelectPopover
              label='募集形態'
              currentValue={recruitmentType}
              options={recruitmentTypeOptions}
              onSelect={(value) => setRecruitmentType(toRecruitmentTypeFormValue(value))}
              header='募集形態を選択'
              contentClassName='w-36'
              triggerVariant='button'
            />
          </div>
        </div>
      )}
    </AppDialog>
  )
}
